import { KeyboardEvent, ReactNode, useRef } from 'react';
import { cn } from '../../../utils/cn';

/**
 * tabs: [{ value, label, count? }]
 * variant "underline" (blue bar under the active tab) or "pills".
 */
export interface TabItem<V extends string = string> {
  value: V;
  label: ReactNode;
  count?: number | null;
}

export interface TabsProps<V extends string = string> {
  tabs: TabItem<V>[];
  value: V;
  onChange?: (value: V) => void;
  variant?: 'underline' | 'pills';
  fullWidth?: boolean;
  className?: string;
  label?: string;
}

export function Tabs<V extends string = string>({ tabs, value, onChange, variant = 'underline', fullWidth, className, label = 'Tabs' }: TabsProps<V>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: KeyboardEvent, i: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
    if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    refs.current[next]?.focus();
    onChange?.(tabs[next].value);
  };

  const pills = variant === 'pills';
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'scrollbar-none flex overflow-x-auto',
        pills ? 'gap-1 rounded-lg border border-line bg-navy-50 p-1' : 'gap-1 border-b border-line',
        fullWidth && 'w-full',
        pills && !fullWidth && 'w-fit max-w-full',
        className,
      )}
    >
      {tabs.map((t, i) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="tab"
            id={`tab-${t.value}`}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange?.(t.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              'relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap text-sm font-medium outline-none transition-colors',
              'focus-visible:ring-2 focus-visible:ring-steel focus-visible:ring-offset-1',
              fullWidth && 'flex-1',
              pills
                ? cn('h-8 rounded-md px-3', active ? 'bg-white text-navy-800 shadow-soft' : 'text-ink-500 hover:text-navy-800')
                : cn('h-11 rounded-t-lg px-3', active ? 'text-brand-700' : 'text-ink-500 hover:text-navy-800'),
            )}
          >
            {t.label}
            {t.count != null && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs font-semibold tabular',
                  active ? 'bg-brand-600 text-white' : 'bg-navy-100 text-ink-500',
                )}
              >
                {t.count}
              </span>
            )}
            {!pills && active && <span className="absolute inset-x-2 -bottom-px h-[2px] rounded-full bg-brand-600" />}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
